import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import classNames from "classnames";
import billingPlans from "@/statics/billingPlans.json";
import { SELFSERVICE_PLANS, PLAN_MODE } from "@/utilities/constants";
import { plansDataExtension } from "@/pages/account/views/subscription-center/data/PlansDataExtension";
import OpticksPlanPillIndicator from "@/components/ui/OpticksPlanPillIndicator/OpticksPlanPillIndicator";
import OpticksPricingWidget from "@/components/ui/OpticksWidgetsStacked/OpticksPricing/OpticksPricingWidget";
import OpticksButtonGroup from "@/components/ui/OpticksButtonGroup/OpticksButtonGroup";
import OpticksDropdownSingleOption from "@/components/ui/dropdowns/single-option/OpticksDropdownSingleOption";
import PointerIcon from "@/components/ui/OpticksIcons/PointerIcon";
import UserIcon from "@/components/ui/OpticksIcons/UserIcon";
import ShieldIcon from "@/components/ui/OpticksIcons/ShieldIcon";
import AnalyzeIcon from "@/components/ui/OpticksIcons/AnalyzeIcon";
import CoinsIcon from "@/components/ui/OpticksIcons/CoinsIcon";
import CreditCardIcon from "@/components/ui/OpticksIcons/CreditCardIcon";
import type { MenuItem } from "@/components/ui/dropdowns/OpticksDropdown.types";
import "./SelfservicePlanSelector.css";

const FREE_TRIAL_DATA = {
  title: "Start exploring Opticks for free",
  features: [
    { icon: <PointerIcon />, title: "Up to 1k monthly scans" },
    { icon: <UserIcon />, title: "1 user" },
    { icon: <ShieldIcon />, title: "Detection only" },
    { icon: <AnalyzeIcon />, title: "Real-time Analytics" },
  ],
};

const CURRENCIES: MenuItem[] = [
  { label: "€ EUR", value: "EUR" },
  { label: "$ USD", value: "USD" },
  { label: "د.إ AED", value: "AED" },
  { label: "﷼ SAR", value: "SAR" },
];

const FREQUENCY_OPTIONS = [
  { label: "Monthly", key: "monthly" },
  { label: "Yearly", key: "yearly" },
];

const TOTAL = SELFSERVICE_PLANS.length;

/**
 * Circular offset: wraps around so offset is always in [-floor(n/2), +floor(n/2)]
 */
const circularOffset = (index: number, activeIndex: number): number => {
  let off = index - activeIndex;
  const half = Math.floor(TOTAL / 2);
  if (off > half) off -= TOTAL;
  if (off < -half) off += TOTAL;
  return off;
};

const getCardStyle = (offset: number): React.CSSProperties => {
  const absOffset = Math.abs(offset);

  if (absOffset === 0) {
    return {
      transform: "translateX(0) scale(1) rotateY(0deg)",
      zIndex: 4,
      opacity: 1,
      filter: "none",
    };
  }

  if (absOffset >= 2) {
    return {
      transform: "translateX(0) scale(0.6) rotateY(180deg)",
      zIndex: 1,
      opacity: 0,
      pointerEvents: "none",
    };
  }

  const dir = offset > 0 ? 1 : -1;
  return {
    transform: `translateX(${dir * 68}%) scale(0.85) rotateY(${dir * -20}deg)`,
    zIndex: 2,
    opacity: 0.7,
    filter: "brightness(0.85)",
  };
};

const SelfservicePlanSelector = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activePlan = searchParams.get("plan") || "lite";
  const currency = searchParams.get("currency") || "EUR";
  const frequency = searchParams.get("frequency") || "monthly";
  const activeIndex = SELFSERVICE_PLANS.indexOf(activePlan);

  const currencyOption =
    CURRENCIES.find((c) => c.value === currency) || CURRENCIES[0];

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1280);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1280);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handlePlanClick = (planType: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("plan", planType);
    setSearchParams(newParams, { replace: true });
  };

  const handleFrequencyChange = (key: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("frequency", key);
    setSearchParams(newParams, { replace: true });
  };

  const handleCurrencyChange = (item: MenuItem) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("currency", String(item.value));
    setSearchParams(newParams, { replace: true });
  };

  return (
    <div className="selfservice-plan-selector">
      <h1>Choose your plan</h1>

      <div className="carousel-controls">
        <div className="carousel-controls-row">
          <OpticksButtonGroup
            options={FREQUENCY_OPTIONS}
            selectedOption={frequency}
            onSelectionChange={handleFrequencyChange}
            surface="white"
            size="s"
          />
          <OpticksDropdownSingleOption
            menuItems={CURRENCIES}
            optionSelected={currencyOption}
            handleChosenOptionChange={handleCurrencyChange}
            width="100px"
          />
        </div>
        <div
          className={classNames("yearly-discount-badge", {
            active: frequency === "yearly",
          })}
        >
          <CoinsIcon />
          <span>Save 10% on Yearly Plans</span>
        </div>
      </div>

      <div className="carousel-scene">
        <div className="carousel-track">
          {SELFSERVICE_PLANS.map((planType, index) => {
            const isActive = activePlan === planType;
            const isFree = planType === PLAN_MODE.FREE;
            const offset = circularOffset(index, activeIndex);

            if (isFree) {
              return (
                <div
                  key={planType}
                  className={classNames(
                    "carousel-card",
                    "carousel-card-free",
                    `type-${planType}`,
                    { "is-active": isActive }
                  )}
                  style={isMobile ? {} : getCardStyle(offset)}
                  onClick={() => handlePlanClick(planType)}
                >
                  <div className="widget-titles">
                    <OpticksPlanPillIndicator
                      planType={PLAN_MODE.FREE}
                      height={28}
                    />
                    <p className="title">{FREE_TRIAL_DATA.title}</p>
                  </div>

                  <div className="widget-features">
                    {FREE_TRIAL_DATA.features.map((feature, i) => (
                      <div className="point" key={i}>
                        <div className="icon">{feature.icon}</div>
                        <div className="text">
                          <span className="feature-title">{feature.title}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="no-cc-required">
                    <CreditCardIcon size={16} /> No credit card required
                  </p>
                </div>
              );
            }

            const ext = plansDataExtension[planType];
            if (!ext) return null;
            return (
              <div
                key={planType}
                className={classNames("carousel-card", `type-${planType}`, {
                  "is-active": isActive,
                })}
                style={isMobile ? {} : getCardStyle(offset)}
                onClick={() => handlePlanClick(planType)}
              >
                <OpticksPricingWidget
                  type={planType}
                  typePill={
                    <OpticksPlanPillIndicator planType={planType} height={28} />
                  }
                  title={ext.title}
                  features={ext.features}
                  frequency={frequency}
                  currency={currency}
                  prices={
                    (
                      billingPlans as Record<
                        string,
                        Record<string, Record<string, number>>
                      >
                    )[planType] || {}
                  }
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="carousel-dots">
        {SELFSERVICE_PLANS.map((planType) => (
          <button
            key={planType}
            className={classNames("carousel-dot", {
              "is-active": activePlan === planType,
            })}
            onClick={() => handlePlanClick(planType)}
            aria-label={`Select ${planType} plan`}
          />
        ))}
      </div>
    </div>
  );
};

export default SelfservicePlanSelector;
